import React, {
  memo,
  MouseEventHandler,
  ReactNode,
  useMemo,
  useState,
} from 'react';
import { Modal, Steps, Button, StepProps } from 'antd';
import { useTranslation } from 'react-i18next';

export interface IStep extends StepProps {
  content: ReactNode;
  title?: string;
  doNotDisplay?: boolean | number | 0;
  disabled?: boolean;
  loading?: boolean;
  config?: StepProps;
}

interface IModalOfSteps {
  isOpen: boolean;
  onConfirm: () => void;
  onClose: MouseEventHandler<HTMLElement>;
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  steps?: IStep[];
  completionStep?: ReactNode;
  title?: string;
  alternativeConfirmTextType?: string;
  confirmLoading?: boolean;
  confirmDisabled?: boolean;
  width?: string;
  allowStepsLine?: boolean;
}

const StepsModal = ({
  title,
  isOpen,
  steps,
  completionStep,
  confirmLoading,
  confirmDisabled,
  onConfirm,
  onClose,
  width,
  alternativeConfirmTextType,
  allowStepsLine,
}: IModalOfSteps) => {
  const { t } = useTranslation('general');
  const [currentStep, setCurrentStep] = useState(0);

  const stepsAvailable = useMemo(
    (): IStep[] => steps?.filter(item => !item.doNotDisplay) ?? [],
    [steps],
  );

  const buttons = useMemo(
    () => [
      {
        textType: 'return',
        visible: currentStep > 0,
        onClick: () => setCurrentStep(currentStep - 1),
      },
      {
        textType: 'next',
        visible: currentStep < stepsAvailable.length - 1,
        loading: stepsAvailable?.[currentStep]?.loading,
        disabled: stepsAvailable?.[currentStep]?.disabled,
        onClick: () => setCurrentStep(currentStep + 1),
      },
      {
        textType: alternativeConfirmTextType ?? 'confirm',
        visible: currentStep === stepsAvailable.length - 1,
        loading: confirmLoading,
        disabled: confirmDisabled,
        onClick: () => {
          onConfirm();
          completionStep && setCurrentStep(currentStep + 1);
        },
      },
    ],
    [
      alternativeConfirmTextType,
      completionStep,
      confirmDisabled,
      confirmLoading,
      currentStep,
      onConfirm,
      stepsAvailable,
    ],
  );

  const footer = useMemo(
    () =>
      currentStep < stepsAvailable?.length &&
      buttons?.map(
        ({ textType, visible, ...rest }) =>
          visible && (
            <Button
              type={
                ['next', 'confirm'].includes(textType) ||
                (alternativeConfirmTextType &&
                  textType === alternativeConfirmTextType)
                  ? 'primary'
                  : 'default'
              }
              {...rest}
            >
              {t(`buttons.${textType}`)}
            </Button>
          ),
      ),
    [alternativeConfirmTextType, buttons, currentStep, stepsAvailable, t],
  );

  return (
    <Modal
      title={title}
      open={isOpen}
      width={width ?? '600px'}
      onCancel={onClose}
      footer={footer}
    >
      {allowStepsLine && currentStep < stepsAvailable?.length && (
        <>
          <Steps
            current={currentStep}
            size="small"
            items={stepsAvailable?.map(item => ({ title: item.title }))}
            style={{ marginBottom: 24 }}
          />
        </>
      )}
      <div className="modal-of-steps-content" style={{ padding: 24 }}>
        {currentStep === stepsAvailable?.length &&
          completionStep &&
          completionStep}
        {currentStep < stepsAvailable?.length &&
          stepsAvailable?.[currentStep]?.content}
      </div>
    </Modal>
  );
};

export default memo(StepsModal);
